import React, { useEffect, useRef, useState } from "react";

/**
 * Wrapper that toggles `.in-view` on its child once it scrolls into the
 * viewport. Pairs with `.reveal-on-scroll` in ledger.css so the
 * animation plays in the reader's eye, not before they arrive.
 *
 * Props:
 *   delay  - 0..4 (matches CSS .delay-1 ... .delay-4)
 *   as     - element tag, defaults to div
 *   amount - 0..1, intersection ratio threshold (default 0.18)
 *   once   - only animate once (default true)
 *   className - extra classes appended to the wrapper
 */
export default function InViewReveal({
  children,
  delay = 0,
  as: Tag = "div",
  amount = 0.18,
  once = true,
  className = "",
  style,
  ...rest
}) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            if (once) obs.disconnect();
          } else if (!once) {
            setShown(false);
          }
        }
      },
      { threshold: amount, rootMargin: "0px 0px -8% 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [amount, once]);

  const cls = [
    "reveal-on-scroll",
    delay ? `delay-${delay}` : "",
    shown ? "in-view" : "",
    className,
  ].filter(Boolean).join(" ");

  return (
    <Tag ref={ref} className={cls} style={style} {...rest}>
      {children}
    </Tag>
  );
}
